// Balance tables — the single source of truth for every number in docs/GDD.md (signed off 2026-09-26).
// Units: money in $M, time in seconds, distance in map units (mu: a duel scales its two capitals 100 mu apart, a
// world war uses one fixed scale for the whole globe; GAME_MODES).
// The sim converts seconds to ticks at load; nothing else may hardcode these values.

import { TRANSPORT_MODE_KEYS, type AiTier, type BuildingKey, type DeployDoctrine, type GameModeKey, type LaunchDoctrine, type MissileGuidance, type StrikeKey, type StrikeTargeting, type TargetClass, type TaxSetting, type TransportModeKey, type UnitKey } from './keys'

// ---------------------------------------------------------------------------------------------- economy

export const ECONOMY = {
  startingTreasury: 1000,
  debtCeiling: 2500,
  capitalRevenuePerSec: 30,
  cityRevenuePerSec: 6,
  overtimeRevenueMultiplier: 2,
  startingApproval: 60,
  highApprovalThreshold: 80,
  highApprovalRevenueMultiplier: 1.1,
  lowApprovalThreshold: 25,
  lowApprovalRevenueMultiplier: 0.8,
} as const

export interface TaxDef {
  readonly revenueMultiplier: number
  readonly approvalPerSec: number
}

export const TAX: Readonly<Record<TaxSetting, TaxDef>> = {
  relief: { revenueMultiplier: 0.6, approvalPerSec: 0.5 },
  low: { revenueMultiplier: 0.8, approvalPerSec: 0.2 },
  standard: { revenueMultiplier: 1.0, approvalPerSec: 0 },
  high: { revenueMultiplier: 1.25, approvalPerSec: -0.3 },
  wartime: { revenueMultiplier: 1.5, approvalPerSec: -0.7 },
}

/** Approval deltas applied by events (GDD §6). Continuous effects live with their source. */
export const APPROVAL_EVENTS = {
  cityCaptured: 5,
  cityLost: -8,
  ownCityStruck: -2,
  ownLandOrSeaUnitLost: -0.5,
  firedShockAndAwe: -3,
} as const

// ---------------------------------------------------------------------------------------------- units

export interface UnitDefBase {
  readonly cost: number
  readonly bodies: number
  readonly hpPerBody: number
  readonly damage: number
  readonly attackPeriod: number
  readonly range: number
  readonly minRange: number
  readonly speed: number
  readonly targetClass: TargetClass
  readonly splashRadius: number
  readonly sight: number
  /** Deploy site requirement beyond "an owned city" (GDD §7). */
  readonly deployRequires: 'none' | 'airbase_or_capital' | 'port'
  /** Which of the cities that can launch it the unit appears in (GDD §7.1). */
  readonly doctrine: DeployDoctrine
  readonly spawner: { readonly unit: UnitKey; readonly interval: number; readonly maxAlive: number } | null
}

/**
 * A ground unit (GDD §4): the only domain that can capture cities, and the only domain automatic transports carry. Its
 * cargo weight is `bodies × cargoSlotsPerBody` living bodies against a craft's TRANSPORT_MODES cargoSlotsPerCraft.
 */
export interface LandUnitDef extends UnitDefBase {
  readonly domain: 'land'
  readonly captures: boolean
  /** Transport cargo slots one living body of this unit occupies; positive, so a full squad never weighs nothing. */
  readonly cargoSlotsPerBody: number
}

/** A unit that fights over water or air: it never captures, and it is never cargo (illegal states unrepresentable). */
export interface NonLandUnitDef extends UnitDefBase {
  readonly domain: 'sea' | 'air'
  readonly captures: false
}

/** Land units can capture and be carried; sea and air units can do neither (GDD §4). */
export type UnitDef = LandUnitDef | NonLandUnitDef

const UNIT_SIGHT = 10
const AIR_SIGHT = 12

export const UNITS: Readonly<Record<UnitKey, UnitDef>> = {
  conscripts: { cost: 200, domain: 'land', bodies: 5, hpPerBody: 60, damage: 12, attackPeriod: 1.0, range: 1.5, minRange: 0, speed: 3.0, targetClass: 'ground', splashRadius: 0, sight: UNIT_SIGHT, captures: true, cargoSlotsPerBody: 1, deployRequires: 'none', doctrine: 'frontline', spawner: null },
  tank: { cost: 700, domain: 'land', bodies: 1, hpPerBody: 1800, damage: 90, attackPeriod: 1.5, range: 3, minRange: 0, speed: 2.0, targetClass: 'structures_only', splashRadius: 0, sight: UNIT_SIGHT, captures: true, cargoSlotsPerBody: 3, deployRequires: 'none', doctrine: 'richest', spawner: null },
  artillery: { cost: 600, domain: 'land', bodies: 1, hpPerBody: 500, damage: 140, attackPeriod: 3.0, range: 12, minRange: 3, speed: 2.0, targetClass: 'ground', splashRadius: 2.0, sight: UNIT_SIGHT, captures: true, cargoSlotsPerBody: 2, deployRequires: 'none', doctrine: 'rearmost', spawner: null },
  mobile_sam: { cost: 400, domain: 'land', bodies: 1, hpPerBody: 450, damage: 110, attackPeriod: 1.2, range: 9, minRange: 0, speed: 3.0, targetClass: 'air_only', splashRadius: 1.5, sight: UNIT_SIGHT, captures: true, cargoSlotsPerBody: 2, deployRequires: 'none', doctrine: 'capital', spawner: null },
  fighter: { cost: 450, domain: 'air', bodies: 1, hpPerBody: 380, damage: 55, attackPeriod: 0.8, range: 5, minRange: 0, speed: 6.0, targetClass: 'air_and_ground_units', splashRadius: 0, sight: AIR_SIGHT, captures: false, deployRequires: 'airbase_or_capital', doctrine: 'loudest', spawner: null },
  bomber: { cost: 900, domain: 'air', bodies: 1, hpPerBody: 1100, damage: 320, attackPeriod: 2.0, range: 0.5, minRange: 0, speed: 2.5, targetClass: 'structures_only', splashRadius: 2.5, sight: AIR_SIGHT, captures: false, deployRequires: 'airbase_or_capital', doctrine: 'alphabetical', spawner: null },
  drone_swarm: { cost: 300, domain: 'air', bodies: 6, hpPerBody: 70, damage: 20, attackPeriod: 1.0, range: 2, minRange: 0, speed: 4.5, targetClass: 'any', splashRadius: 0, sight: AIR_SIGHT, captures: false, deployRequires: 'airbase_or_capital', doctrine: 'lowest_bidder', spawner: null },
  destroyer: { cost: 800, domain: 'sea', bodies: 1, hpPerBody: 1500, damage: 80, attackPeriod: 1.0, range: 8, minRange: 0, speed: 3.0, targetClass: 'any', splashRadius: 0, sight: UNIT_SIGHT, captures: false, deployRequires: 'port', doctrine: 'frontline', spawner: null },
  carrier: { cost: 1500, domain: 'sea', bodies: 1, hpPerBody: 3000, damage: 0, attackPeriod: 0, range: 0, minRange: 0, speed: 1.5, targetClass: 'none', splashRadius: 0, sight: UNIT_SIGHT, captures: false, deployRequires: 'port', doctrine: 'rearmost', spawner: { unit: 'fighter', interval: 12, maxAlive: 2 } },
}

// ------------------------------------------------------------------------------------------ automatic transport

/** One automatic transport mode's craft (docs/TRANSPORT.md). */
export interface TransportModeDef {
  /** The domain the craft travels and is targeted in: a boat is a surface target, a plane an air target. */
  readonly domain: 'sea' | 'air'
  /**
   * Craft one operational berth of this mode maintains. This is the ONE authoritative count shared by fleet spawning,
   * the status/authorisation readout and render capacity: there is no per-match override and no presentation tuner.
   */
  readonly craftPerBerth: number
  /** Cargo slots one craft holds; a unit occupies `bodies × cargoSlotsPerBody` of them. */
  readonly cargoSlotsPerCraft: number
  readonly hp: number
  /** mu per second while travelling. */
  readonly speed: number
  /** mu; the craft's own sight (it is a normal target and observer of its domain). */
  readonly sight: number
  /** Seconds a berth below its complement waits before a replacement craft is ready. */
  readonly replacementSeconds: number
  /** Routing cost of boarding (positive, so a trip is never free). */
  readonly boardingCost: number
  /** Routing cost of landing (positive, so a trip is never free). */
  readonly landingCost: number
}

/**
 * Automatic transports (taxgw-8lvi, taxgw-uuku, taxgw-rvwq, taxgw-6gcs). A Port maintains boats: one operational port
 * serves every coastal city of its physical sea component; a boat fetches the own booked group it serves first (queue
 * tick + its sail there), lands it at the group's planned shore and picks its next group from there. An Airbase
 * maintains planes: a ground group walks to an own operational airbase, a plane flies there by the same rule, carries
 * the group straight to the city it is heading for and flies home. Neither craft chooses where a group goes. Initial
 * tuning values, not measured balance.
 */
export const TRANSPORT_MODES: Readonly<Record<TransportModeKey, TransportModeDef>> = {
  sea: { domain: 'sea', craftPerBerth: 2, cargoSlotsPerCraft: 8, hp: 600, speed: 1.8, sight: 10, replacementSeconds: 30, boardingCost: 4, landingCost: 4 },
  air: { domain: 'air', craftPerBerth: 2, cargoSlotsPerCraft: 8, hp: 450, speed: 12, sight: 12, replacementSeconds: 30, boardingCost: 4, landingCost: 4 },
}

/** How the port-aware ground planner costs and chooses transport legs (docs/TRANSPORT.md). */
export const TRANSPORT_ROUTING = {
  /** mu per second the fastest ground unit walks: the reference speed every transport leg is re-costed by, so a
   * craft's leg is measured in reference-land-distance units and never reads as faster than it really is. */
  routingGroundSpeed: 3,
  /**
   * Max ground goal trees (and as many airlift trees) kept per seat: a bounded cache, recomputed when berths or the
   * seat's transport waits change. Sized for the distinct objectives a seat's standing groups head for at once.
   */
  groundPlannerGoalTreesPerSeat: 32,
  /**
   * A ground group flies only when its ground route (walking and ferries) costs at least this multiple of the airlift
   * route: 1.5 means the ground route must take 50% longer, so a marginal saving never ties up a plane.
   */
  airliftMinGroundRatio: 1.5,
  /**
   * Route choice prices each transport pool's expected wait for a craft (taxgw-6gcs; docs/TRANSPORT.md): the booked work
   * ahead divided by the pool's craft. Waits shorter than this count as none: a craft is about to come anyway.
   */
  waitFloorSeconds: 5,
  /** Waits are priced in geometric bands this wide (1.25 = 25%), so a route choice only moves when a wait really does. */
  waitLevelRatio: 1.25,
  /** A pool's priced wait changes only on ticks that are multiples of this: the bound on the planner rebuilds waits can cause. */
  waitRefreshSeconds: 1,
  /** A falling wait only drops a band once it is this fraction of the band's floor (hysteresis against flapping). */
  waitLevelHysteresis: 0.9,
  /**
   * Just-in-time pickup: an idle craft may set out for a group still walking to its pickup when that group is expected
   * there no later than this many seconds after the craft, so the craft does not wait for the group.
   */
  justInTimeSlackSeconds: 5,
  /**
   * A fleet whose wait for a craft passes this is backlogged: one more berth of its mode (another Airbase, or another
   * Port on that water) is worth its slot. The HUD tells the player so and the AI builds one.
   */
  backlogWaitSeconds: 45,
} as const

// ---------------------------------------------------------------------------------------------- structures

export interface CityGunDef {
  readonly damage: number
  readonly period: number
  readonly range: number
}

export interface CityDef {
  readonly hp: number
  /** The defence gun; null for neutral territory, which takes no part in the war until claimed (GDD §5). */
  readonly gun: CityGunDef | null
  readonly sight: number
}

export const CITIES = {
  owned: { hp: 2000, gun: { damage: 60, period: 0.8, range: 8 }, sight: 10 },
  capital: { hp: 4500, gun: { damage: 90, period: 1.0, range: 9 }, sight: 12 },
  neutral: { hp: 800, gun: null, sight: 0 },
} as const satisfies Record<string, CityDef>

export const CAPTURE = {
  radius: 3,
  uncontestedSeconds: 3,
  rejoinHpFraction: 0.4,
  /** Building slots per non-capital city and for the capital (GDD §7). */
  citySlots: 1,
  capitalSlots: 2,
} as const

/** The largest craft complement any one berth maintains, across every transport mode. */
const MAX_CRAFT_PER_BERTH = Math.max(...TRANSPORT_MODE_KEYS.map((mode) => TRANSPORT_MODES[mode].craftPerBerth))

/**
 * Upper bound on transport craft alive at once, every mode and orphan voyages included: every map city with the
 * largest berth slot count, each berth maintaining the largest complement. Server admission and client batch sizing
 * share this bound (the sim's transport logistics use the same arithmetic); it is a per-match capacity contract, not a
 * measured capacity.
 */
export function transportCraftCapacity(cityCount: number): number {
  return cityCount * Math.max(CAPTURE.citySlots, CAPTURE.capitalSlots) * MAX_CRAFT_PER_BERTH
}

// ---------------------------------------------------------------------------------------------- buildings

export type BuildingEffect =
  | { readonly kind: 'revenue'; readonly perSec: number }
  | { readonly kind: 'deploy_air' }
  | { readonly kind: 'deploy_sea'; readonly coastalOnly: true }
  | { readonly kind: 'sam_site'; readonly hp: number; readonly damage: number; readonly period: number; readonly range: number; readonly interceptPeriod: number; readonly sight: number }
  | { readonly kind: 'fortify'; readonly hpMultiplier: number; readonly gunDamageMultiplier: number }
  | { readonly kind: 'approval'; readonly perSec: number }

export interface BuildingDef {
  readonly cost: number
  readonly effect: BuildingEffect
}

export const BUILDINGS: Readonly<Record<BuildingKey, BuildingDef>> = {
  tax_office: { cost: 400, effect: { kind: 'revenue', perSec: 6 } },
  airbase: { cost: 500, effect: { kind: 'deploy_air' } },
  port: { cost: 400, effect: { kind: 'deploy_sea', coastalOnly: true } },
  sam_site: { cost: 600, effect: { kind: 'sam_site', hp: 900, damage: 100, period: 1.0, range: 11, interceptPeriod: 6, sight: 14 } },
  fortress: { cost: 700, effect: { kind: 'fortify', hpMultiplier: 1.5, gunDamageMultiplier: 1.6 } },
  propaganda_office: { cost: 350, effect: { kind: 'approval', perSec: 0.2 } },
}

// ---------------------------------------------------------------------------------------------- strikes

/** Every strike deals this fraction of its damage to structures (GDD §9). */
export const STRIKE_STRUCTURE_DAMAGE_FRACTION = 0.4

export type StrikeEffect =
  | {
      readonly kind: 'missile'
      readonly damage: number
      readonly radius: number
      readonly salvos: number
      /** Between salvos, counted from the missile's arrival (salvo 0 lands as it arrives). */
      readonly salvoIntervalSeconds: number
      /** Map units per second at every range (GDD §7.1): the flight takes as long as its distance needs. */
      readonly speed: number
      readonly guidance: MissileGuidance
      readonly selfApproval: number
      /** GDD §7.1: what it hits, and which own city it launches from. */
      readonly targeting: StrikeTargeting
      readonly launchFrom: LaunchDoctrine
    }
  | { readonly kind: 'sanctions'; readonly revenueMultiplier: number; readonly durationSeconds: number; readonly enemyApproval: number }
  | {
      readonly kind: 'leaflets'
      readonly enemyApproval: number
      readonly radius: number
      readonly damageMultiplier: number
      readonly durationSeconds: number
      /** GDD §7.1: where the leaflets land. */
      readonly targeting: StrikeTargeting
    }

export interface StrikeDef {
  readonly cost: number
  readonly interceptable: boolean
  readonly effect: StrikeEffect
}

export const STRIKES: Readonly<Record<StrikeKey, StrikeDef>> = {
  precision_strike: { cost: 600, interceptable: true, effect: { kind: 'missile', damage: 600, radius: 1.5, salvos: 1, salvoIntervalSeconds: 1, speed: 20, guidance: 'homing', selfApproval: 0, targeting: 'priciest', launchFrom: 'nearest' } },
  shock_and_awe: { cost: 1200, interceptable: true, effect: { kind: 'missile', damage: 350, radius: 5, salvos: 3, salvoIntervalSeconds: 1, speed: 30, guidance: 'point', selfApproval: APPROVAL_EVENTS.firedShockAndAwe, targeting: 'crowd', launchFrom: 'farthest' } },
  sanctions: { cost: 700, interceptable: false, effect: { kind: 'sanctions', revenueMultiplier: 0.7, durationSeconds: 25, enemyApproval: -4 } },
  leaflet_drop: { cost: 500, interceptable: false, effect: { kind: 'leaflets', enemyApproval: -3, radius: 4, damageMultiplier: 0.75, durationSeconds: 8, targeting: 'closest_to_home' } },
}

// ---------------------------------------------------------------------------------------------- AI, clock, limits

export interface AiTierDef {
  readonly reactionDelaySeconds: number
  /** 0..1 probability the tier picks the matching counter when it deploys a response. */
  readonly counterAccuracy: number
  readonly usesTaxDial: boolean
  readonly buildsEconomy: boolean
  readonly usesStrikes: boolean
  readonly plansCombinedPushes: boolean
}

export const AI_TIER_DEFS: Readonly<Record<AiTier, AiTierDef>> = {
  recruit: { reactionDelaySeconds: 4.0, counterAccuracy: 0.35, usesTaxDial: false, buildsEconomy: false, usesStrikes: false, plansCombinedPushes: false },
  veteran: { reactionDelaySeconds: 2.5, counterAccuracy: 0.6, usesTaxDial: false, buildsEconomy: true, usesStrikes: false, plansCombinedPushes: false },
  general: { reactionDelaySeconds: 1.5, counterAccuracy: 0.85, usesTaxDial: true, buildsEconomy: true, usesStrikes: true, plansCombinedPushes: false },
  warlord: { reactionDelaySeconds: 0.8, counterAccuracy: 0.9, usesTaxDial: true, buildsEconomy: true, usesStrikes: true, plansCombinedPushes: true },
}

/** Bounds shared by every mode; each mode sets its own body cap (GameModeDef.bodyCap). */
export const LIMITS = {
  maxStrikesInFlight: 16,
  commandsPerSecondPerPlayer: 10,
  /** Bodies per seat that per-war pools start with under an unlimited body cap; every such pool grows past it. */
  presizedBodiesPerPlayer: 100,
} as const

// ---------------------------------------------------------------------------------------------- game modes

/** Which cities a war is fought over and how its distances are scaled (GDD §3, §4). */
export type WarMapRules =
  | {
      readonly kind: 'duel'
      // Each belligerent fields every city of its country (user, 2026-09-28): a nation with fewer cities is the
      // harder war, by the player's choice.
      /** Neutral territory: the capitals of other countries nearest the midpoint of the two capitals' great circle. */
      readonly neutralCapitalsMin: number
      readonly neutralCapitalsMax: number
      /** Every duel is scaled so that its two capitals are this far apart. */
      readonly capitalDistanceMu: number
    }
  | {
      readonly kind: 'world'
      /**
       * Every city of the dataset is on the map: each belligerent owns its whole country, every other city is neutral
       * territory. The scale is fixed (mu per radian of great-circle arc), so the board is the same in every war.
       */
      readonly muPerRadian: number
    }

/** Longest extra edge of the network: a multiple of its longest spanning-tree edge, or a fixed arc. */
export type ExtraEdgeLimit = { readonly kind: 'mst_ratio'; readonly ratio: number } | { readonly kind: 'arc_deg'; readonly deg: number }

/** How the seeded network is drawn over the chosen cities (GDD §4): a spanning tree, then seeded short extras. */
export interface NetworkRules {
  /** Nearest non-adjacent candidate neighbours considered per city in the extra-edge pass. */
  readonly extraEdgeCandidatesPerCity: number
  /** Chance that each considered candidate becomes an edge. */
  readonly extraEdgeChance: number
  readonly extraEdgeMax: ExtraEdgeLimit
}

export interface MatchClockDef {
  readonly regulationSeconds: number
  readonly overtimeSeconds: number
}

/** Online war rooms (ARCHITECTURE §15, GDD §10): the timings every online war runs by. There is no pause (D10). */
export const ONLINE_MATCH = {
  /** Every seat must connect within this long of the match being made, or the room closes without a war. */
  joinWindowSeconds: 60,
  /** Once every seat is connected, the war starts after this countdown (every client has its map up). */
  startCountdownSeconds: 3,
  /** A seat whose connection drops mid-war surrenders when it stays away this long. */
  reconnectGraceSeconds: 30,
} as const

/**
 * How far a mode is through release (ROADMAP D30): 'released' modes are offered everywhere; 'preview' modes play in
 * development builds only, and the live site shows them as a coming-soon teaser.
 */
export type ReleaseStage = 'released' | 'preview'

/**
 * A seat's living-body limit (GDD §7): a performance bound, not a pacing tool. 'unlimited' admits every unit the
 * treasury affords; money alone paces the army.
 */
export type BodyCap = { readonly kind: 'capped'; readonly bodiesPerPlayer: number } | { readonly kind: 'unlimited' }

/** Whether a seat with `alive` living bodies may field `bodies` more under `cap`: the one body-cap rule. */
export function bodyCapAdmits(cap: BodyCap, alive: number, bodies: number): boolean {
  return cap.kind === 'unlimited' || alive + bodies <= cap.bodiesPerPlayer
}

/** Bodies per seat a per-war pool starts with: the cap itself, or LIMITS.presizedBodiesPerPlayer when unlimited. */
export function presizedBodies(cap: BodyCap): number {
  return cap.kind === 'capped' ? cap.bodiesPerPlayer : LIMITS.presizedBodiesPerPlayer
}

export interface GameModeDef {
  readonly release: ReleaseStage
  readonly map: WarMapRules
  readonly network: NetworkRules
  readonly clock: MatchClockDef
  /** Nations in one war (seats). Both modes are two-nation wars today; FFA / Axis vs Allies raise the maximum. */
  readonly belligerentsMin: number
  readonly belligerentsMax: number
  /** Living unit bodies per seat (GDD §7). */
  readonly bodyCap: BodyCap
  /**
   * What a city that was neutral at the war's start (free territory, GDD §5) is worth next to a homeland city, whoever
   * holds it: its revenue (base and revenue buildings) and the approval its claim, capture, loss or a strike on it moves
   * are scaled by this weight (GDD §6). 1 = a homeland city.
   */
  readonly territoryWeight: number
  /**
   * Scale on every approval event (captures, losses, strikes, casualties; GDD §6), not on the per-second drift: a long
   * war's public reacts to each headline less. 1 = the duel's reactions.
   */
  readonly approvalEventScale: number
  /**
   * How strongly capturers expand toward the enemy (GDD §8 step 4): free territory's route cost as an objective is
   * raised by this multiple of its own route cost to the nearest enemy city. 0 = the nearest free city; toward 1, only
   * free cities on the way to the enemy.
   */
  readonly expansionEnemyPull: number
}

export const GAME_MODES: Readonly<Record<GameModeKey, GameModeDef>> = {
  duel: {
    release: 'released',
    map: { kind: 'duel', neutralCapitalsMin: 3, neutralCapitalsMax: 8, capitalDistanceMu: 100 },
    network: { extraEdgeCandidatesPerCity: 3, extraEdgeChance: 0.4, extraEdgeMax: { kind: 'mst_ratio', ratio: 1 } },
    clock: { regulationSeconds: 7 * 60, overtimeSeconds: 2 * 60 },
    belligerentsMin: 2,
    belligerentsMax: 2,
    // Unlimited for now (user, 2026-09-30, taxgw-r4mc); the duel's cap was 60.
    bodyCap: { kind: 'unlimited' },
    territoryWeight: 1,
    approvalEventScale: 1,
    expansionEnemyPull: 0,
  },
  world_war: {
    // Preview until its balance pass (taxgw-jk1f): the live site shows it as coming soon.
    release: 'preview',
    // 800 mu per radian puts the median network edge near 19 mu, like a duel's (docs/GAME-MODES-ROADMAP.md §1 D25).
    map: { kind: 'world', muPerRadian: 800 },
    network: { extraEdgeCandidatesPerCity: 3, extraEdgeChance: 0.5, extraEdgeMax: { kind: 'arc_deg', deg: 6 } },
    clock: { regulationSeconds: 20 * 60, overtimeSeconds: 3 * 60 },
    belligerentsMin: 2,
    belligerentsMax: 2,
    // Balance pass (taxgw-jk1f, ROADMAP D31): ~1,250 free cities must not snowball taxes or approval, a 20-minute war
    // must not be decided by piling up approval headlines, and armies must expand toward each other instead of filling
    // their own continents. Body cap unlimited for now (user, 2026-09-30, taxgw-r4mc); it was 100.
    bodyCap: { kind: 'unlimited' },
    territoryWeight: 0,
    approvalEventScale: 0.35,
    expansionEnemyPull: 0.75,
  },
}
